import * as React from "react";
import {
  Typography,
  Box,
  Button,
  Grid,
  MenuItem,
} from "@mui/material";
import { connect } from "react-redux";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  MainInput,
  centered_flex_box,
  left_flex_box,
  main_button,
} from "../app/components/Styles";
import { notification, Space, Table } from "antd";
import ImageComponent from "../app/components/ImageComponent";
import { getOrders, addReturn } from "../app/store/actions/dataActions";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CreateTicketModal from "../app/components/CreateTicketModal";

const MAX_REFUND_EGP = 4000;

export const ReturnOrder = ({
  getOrders,
  order,
  addReturn,
  isLoading,
  token
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [helpTicketOpen, setHelpTicketOpen] = React.useState(false);
  const { t } = useTranslation();
  const [orderItems, setOrderItems] = useState([]);
  const [refundAmount, setRefundAmount] = useState(0);
  const [reason, setReason] = useState("");

  useEffect(() => {
    getOrders({ orderId: id })
  }, [id])

  const calculatedRefund = useMemo(() => {
    if (!order?.orderItems?.length || orderItems.length === 0) return 0;
    return orderItems.reduce((sum, item) => {
      const orderItem = order.orderItems.find((oi) => oi._id === item._id);
      const price = parseFloat(orderItem?.stockItem?.product?.price) || 0;
      const quantity = parseInt(item.quantity, 10) || 0;
      return sum + (price * quantity);
    }, 0);
  }, [orderItems, order]);

  useEffect(() => {
    setRefundAmount(calculatedRefund);
  }, [calculatedRefund]);

  const parsedRefundAmount = parseFloat(refundAmount) || 0;
  const exceedsMaxRefund = parsedRefundAmount > MAX_REFUND_EGP;

  const handleSubmit = () => {
    if (exceedsMaxRefund) {
      notification.error({
        message: t("returnOrder.maxRefundExceeded", { max: MAX_REFUND_EGP }),
      });
      return;
    }
    addReturn({ orderId: id, orderItems, refundAmount: parsedRefundAmount, reason }, navigate)
  }

  const columns = [
    {
      title: t("common.sku"),
      dataIndex: 'externalId',
    },
    {
      title: t("common.image"),
      dataIndex: 'image',
      render: (text) => <ImageComponent width={50} height={50} src={text} preview={true} />,
    },
    {
      title: t("common.name"),
      dataIndex: 'name',
    },
    {
      title: t("common.price"),
      dataIndex: 'price',
      render: (text) => `${text} EGP`,
    },
    {
      title: t("common.size"),
      dataIndex: 'size',
    },
    {
      title: t("orders.ordered"),
      dataIndex: 'quantityOrdered',
      sorter: (a, b) => a.quantityOrdered - b.quantityOrdered,
    },
    {
      title: t("returnOrder.qtyToReturn"),
      render: (_, { _id, quantityReturnable }) => <Space>
        <MainInput
          margin="normal"
          type="number"
          fullWidth
          id={_id}
          disabled={orderItems.filter((item) => item._id === _id).length === 0}
          inputProps={{
            min: orderItems.filter((item) => item._id === _id).length > 0 ? 1 : 0,
            max: quantityReturnable
          }}
          value={orderItems.filter((item) => item._id === _id).length > 0 ? orderItems.filter((item) => item._id === _id)[0].quantity : 0}
          onChange={({ target }) => onQuantityChange(_id, target.value)}
        />
      </Space>
    },
  ];

  const data = order?.orderItems?.map((row) => {
    const quantityReturned = row.quantityReturned || 0;
    return {
      _id: row._id,
      key: row._id,
      externalId: row.stockItem.product.sku || row.stockItem.product.externalId,
      price: row.stockItem.product.price,
      image: row.stockItem.product.image,
      name: row.stockItem.product.name,
      size: row.stockItem.product.size,
      color: row.stockItem.product.color,
      weight: row.stockItem.product.weight,
      quantityOrdered: row.quantityOrdered,
      quantityReturnable: Math.max(0, row.quantityOrdered - quantityReturned),
    }
  })

  const onQuantityChange = (_id, newQuantity) => {
    const newItems = orderItems.map((item) => {
      if (item._id === _id) {
        item.quantity = newQuantity;
      }
      return item
    });
    setOrderItems(newItems);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setOrderItems(newSelectedRowKeys.map((key) => { return { _id: key, quantity: 1 } }));
  };

  const rowSelection = {
    orderItems,
    onChange: onSelectChange,
  };

  const hasSelected = orderItems.length > 0;

  return (
    <div>
      <Box sx={{ width: "100%", padding: 5, mt: 2 }}>
        <Box sx={{ ...centered_flex_box, mb: 1, flexWrap: "wrap", gap: 2 }}>
          <Typography variant='h4' sx={{ color: "var(--secColor4)" }}>{t("returnOrder.title", { orderNumber: order?.shopifyOrderNumber || id })}</Typography>
          <Button sx={{ ...main_button, textTransform: "none" }} onClick={() => setHelpTicketOpen(true)}>
            {t("helpDesk.getHelp")}
          </Button>
        </Box>
        <Box sx={{}}>
          <Box sx={{ ...left_flex_box }}>
            <Grid container spacing={2}>
              <Grid item style={{ ...centered_flex_box }} xs={5}>
                <MainInput
                  margin="normal"
                  label={t("returns.returnReason")}
                  required
                  value={reason}
                  fullWidth
                  select
                  onChange={({ target }) => setReason(target.value)}
                >
                  <MenuItem value={"Defective Items"}>{t("home.defectiveItems")}</MenuItem>
                  <MenuItem value={"Customer Errors"}>{t("home.customerIssues")}</MenuItem>
                  <MenuItem value={"Warehouse Mistake"}>{t("home.wrongItems")}</MenuItem>
                </MainInput>
              </Grid>
              <Grid item style={{ ...centered_flex_box }} xs={5}>
                <MainInput
                  margin="normal"
                  type="number"
                  label={t("returnOrder.refundAmountEGP")}
                  disabled={orderItems.length === 0}
                  fullWidth
                  inputProps={{
                    min: 0,
                    max: MAX_REFUND_EGP
                  }}
                  value={refundAmount}
                  error={exceedsMaxRefund}
                  helperText={exceedsMaxRefund
                    ? t("returnOrder.maxRefundExceeded", { max: MAX_REFUND_EGP })
                    : t("returnOrder.refundAutoCalculated")}
                  onChange={({ target }) => setRefundAmount(target.value)}
                />
              </Grid>
              <Grid item style={{ ...centered_flex_box }} xs={2}>
                <Button sx={{ ...main_button }} onClick={handleSubmit} disabled={!hasSelected || exceedsMaxRefund || !reason} loading={isLoading}>
                  {t("returnOrder.sendReturn")}
                </Button>
                <Typography sx={{ marginInlineStart: 1 }}>{hasSelected ? t("returnOrder.selectedItems", { count: orderItems.length }) : null}</Typography>
              </Grid>
            </Grid>
          </Box>
          <Table
            columns={columns}
            dataSource={data}
            showSorterTooltip={{
              target: 'sorter-icon',
            }}
            rowSelection={rowSelection}
          />
        </Box>
      </Box>
      <CreateTicketModal
        open={helpTicketOpen}
        onClose={() => setHelpTicketOpen(false)}
        token={token}
        context={{ ticketType: "Order", relevantOrder: id }}
        onSuccess={() => setHelpTicketOpen(false)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
  token: state?.auth?.token,
  order: state?.records?.order
});

const mapDispatchToProps = { getOrders, addReturn };

export default connect(mapStateToProps, mapDispatchToProps)(ReturnOrder);
